<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import AppSidebar from '@/components/layout/AppSidebar.vue'
import AppTopbar from '@/components/layout/AppTopbar.vue'
import AppToaster from '@/components/ui/AppToaster.vue'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useRealtimeStore } from '@/stores/realtime'
import { useNotificationStore } from '@/stores/notifications'
import { useToastStore } from '@/stores/toast'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const realtime = useRealtimeStore()
const notifications = useNotificationStore()
const toast = useToastStore()
const { initialized } = storeToRefs(auth)

// Instancí se téma aktivuje a hned se propíše na <html>.
useThemeStore()

const sidebarOpen = ref(false)
const isAuthLayout = computed(() => route.meta.layout === 'auth' || !auth.isAuthenticated)

// Spojení k hubu žije jen po dobu přihlášení — po odhlášení nemá co odebírat
// a token by stejně přestal platit.
watch(
  () => auth.isAuthenticated,
  (signedIn) => (signedIn ? realtime.connect() : realtime.reset()),
  { immediate: true },
)

// Server může relaci ukončit (odhlášení ostatních zařízení, smazání účtu);
// otevřená záložka na to musí zareagovat sama.
realtime.on('session.revoked', (message) => {
  const reason = typeof message.data.reason === 'string' ? message.data.reason : null

  auth.clear()
  notifications.reset()
  realtime.reset()
  toast.warning(reason ?? 'Vaše relace byla ukončena.')
  void router.push({ name: 'login' })
})

onBeforeUnmount(() => realtime.reset())

watch(() => route.fullPath, () => {
  sidebarOpen.value = false
})
</script>

<template>
  <div
    v-if="!initialized"
    class="boot"
  >
    <span
      class="boot__spinner"
      aria-hidden="true"
    />
    <span class="sr-only">Načítám aplikaci</span>
  </div>

  <div
    v-else-if="isAuthLayout"
    class="auth-shell"
  >
    <RouterView />
  </div>

  <div
    v-else
    class="shell"
  >
    <AppSidebar
      :open="sidebarOpen"
      @navigate="sidebarOpen = false"
    />

    <div
      v-if="sidebarOpen"
      class="shell__overlay"
      @click="sidebarOpen = false"
    />

    <div class="shell__main">
      <AppTopbar @toggle-sidebar="sidebarOpen = !sidebarOpen" />
      <main class="shell__content">
        <RouterView v-slot="{ Component }">
          <Transition
            name="page"
            mode="out-in"
          >
            <component :is="Component" />
          </Transition>
        </RouterView>
      </main>
    </div>
  </div>

  <AppToaster />
</template>

<style scoped>
.boot {
  display: grid;
  place-items: center;
  min-height: 100vh;
}

.boot__spinner {
  width: 26px;
  height: 26px;
  border: 2.5px solid var(--border-default);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: boot-spin 0.7s linear infinite;
}

@keyframes boot-spin { to { transform: rotate(360deg); } }

.auth-shell {
  display: grid;
  place-items: center;
  min-height: 100vh;
  padding: var(--space-5);
  background:
    radial-gradient(1000px 500px at 15% -10%, color-mix(in srgb, var(--accent) 12%, transparent), transparent),
    var(--surface-page);
}

.shell { min-height: 100vh; }

.shell__main {
  margin-left: var(--sidebar-width);
  min-height: 100vh;
}

.shell__content {
  padding: var(--space-5);
  max-width: 1360px;
}

.shell__overlay {
  position: fixed;
  inset: 0;
  z-index: 35;
  background: rgba(15, 17, 23, 0.4);
}

.page-enter-active, .page-leave-active { transition: opacity var(--transition); }
.page-enter-from, .page-leave-to { opacity: 0; }

@media (max-width: 900px) {
  .shell__main { margin-left: 0; }
  .shell__content { padding: var(--space-4); }
}
</style>
